// Staff-side enrollment hand-off. Staff opens this screen → optionally
// types whatever the customer is willing to give at the counter → POST
// mints an enrollment token + URL → fullscreen QR for the customer to
// scan. The customer lands on the same /e/:branch_slug enrollment page
// (prefilled with whatever staff captured) and reviews/consents/submits
// from their own phone. Everything is optional — staff can hit Generate
// with no inputs at all and the customer fills in their own details.
//
// Why we don't skip the customer screen entirely: PH Data Privacy Act
// (RA 10173) requires consent from the data subject — the customer's
// own click is what makes their `consented_at` legally valid. Staff
// pre-filling the form is fine; staff consenting on their behalf is not.

import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { QRCodeSVG } from 'qrcode.react'
import { toast } from 'sonner'
import {
  ALL_ONBOARDING_FIELDS,
  ONBOARDING_FIELD_LABELS,
  type MembershipConfig,
  type OnboardingFieldKey,
  type OnboardingFields,
} from '@naya/shared'

import { ownerApi, OwnerApiError } from '../../owner/ownerApi.js'
import { WalletInstallNudge } from '../../shared/WalletInstallNudge.js'
import './staff.css'

interface MintResponse {
  // token + expires_at are null when no prefill data was captured — the
  // server returns the plain public enrollment URL, which never expires.
  token: string | null
  expires_at: string | null
  enroll_url: string
  branch_slug: string
}


interface StaffProgramListItem {
  id: string
  name: string
  is_default: boolean
  // SLICE-MEMBERSHIP-PASS — staff can enroll memberships the owner opted into.
  pass_type: 'loyalty' | 'membership'
  type_config: MembershipConfig | null
  onboarding_fields: OnboardingFields
}

export function StaffEnrollPage() {
  const [values, setValues] = useState<Partial<Record<OnboardingFieldKey, string>>>({})
  const [minted, setMinted] = useState<MintResponse | null>(null)

  // The tenant's active loyalty programs (with their onboarding shape). The
  // program picker is only surfaced when there's more than one; otherwise the
  // mint uses the tenant default. The form fields are re-derived from the
  // SELECTED program so switching updates which inputs render.
  const programsList = useQuery<{ programs: StaffProgramListItem[] }>({
    queryKey: ['staff', 'programs'],
    queryFn: () =>
      ownerApi.get<{ programs: StaffProgramListItem[] }>('/staff/programs'),
    staleTime: 60_000,
  })
  const activePrograms = programsList.data?.programs ?? []
  const multiProgram = activePrograms.length > 1
  const defaultProgram =
    activePrograms.find((p) => p.is_default) ?? activePrograms[0]
  const [selectedProgramId, setSelectedProgramId] = useState<string | null>(null)
  const effectiveProgramId = selectedProgramId ?? defaultProgram?.id ?? null

  const selectedProgram =
    activePrograms.find((p) => p.id === effectiveProgramId) ?? defaultProgram
  const onboarding = selectedProgram?.onboarding_fields
  const selectedIsMembership = selectedProgram?.pass_type === 'membership'

  // SLICE-MEMBERSHIP-PASS — tier options for a tiered membership. Staff pick the
  // member's tier here; it rides on the minted token to card-create. Mirrors the
  // owner enroll page.
  const tierOptions: string[] =
    selectedIsMembership && selectedProgram?.type_config?.tiers_enabled
      ? selectedProgram.type_config.tiers.map((t) => t.trim()).filter(Boolean)
      : []
  const needsTier = tierOptions.length > 0
  const [selectedTier, setSelectedTier] = useState<string>('')
  // Reset the tier whenever the selected program changes.
  useEffect(() => {
    setSelectedTier('')
  }, [effectiveProgramId])

  // Which fields to render: first_name + email are always enabled; the
  // optional fields show only if the owner turned them on for this program.
  const enabledKeys = useMemo<OnboardingFieldKey[]>(() => {
    if (!onboarding) return ['first_name', 'email']
    return ALL_ONBOARDING_FIELDS.filter((k) => onboarding[k].enabled)
  }, [onboarding])

  // Drop captured values for fields the newly-selected program doesn't collect.
  useEffect(() => {
    setValues((prev) => {
      const next: Partial<Record<OnboardingFieldKey, string>> = {}
      for (const k of enabledKeys) {
        if (prev[k] !== undefined) next[k] = prev[k]!
      }
      return next
    })
  }, [enabledKeys])

  const mint = useMutation({
    mutationFn: async () => {
      // Strip empty values — the server-side sanitizer ignores them but
      // sending less keeps the audit log readable.
      const prefilled_fields: Record<string, string> = {}
      for (const [k, v] of Object.entries(values)) {
        const trimmed = (v ?? '').trim()
        if (trimmed.length > 0) prefilled_fields[k] = trimmed
      }
      return ownerApi.post<MintResponse>('/staff/enrollment-tokens', {
        prefilled_fields,
        ...(multiProgram && effectiveProgramId
          ? { program_id: effectiveProgramId }
          : {}),
        // The chosen tier for a tiered membership (validated server-side).
        ...(needsTier && selectedTier
          ? { membership_tier: selectedTier }
          : {}),
      })
    },
    onSuccess: (res) => {
      setMinted(res)
    },
    onError: (err) => {
      const message =
        err instanceof OwnerApiError ? err.message : 'Could not create QR'
      toast.error(message)
    },
  })

  const expiresInMin = useMemo(() => {
    if (!minted?.expires_at) return null
    const diff = new Date(minted.expires_at).getTime() - Date.now()
    return Math.max(0, Math.round(diff / 60_000))
  }, [minted])

  function reset() {
    setMinted(null)
    setValues({})
  }

  if (programsList.isLoading) {
    return (
      <div className="staff-enroll">
        <p className="stamp-hint">Loading enrollment form…</p>
      </div>
    )
  }
  if (programsList.isError) {
    return (
      <div className="staff-enroll">
        <h1>Enrollment unavailable</h1>
        <p className="stamp-error">
          {programsList.error instanceof OwnerApiError
            ? programsList.error.message
            : 'Could not load the loyalty program.'}
        </p>
      </div>
    )
  }

  if (minted) {
    return (
      <div className="staff-enroll-result">
        <h1>Show this QR to the customer</h1>
        <p className="stamp-hint">
          They scan with their own phone to confirm and add the pass to
          their wallet.
        </p>
        <div className="staff-enroll-qr">
          <QRCodeSVG
            value={minted.enroll_url}
            size={280}
            level="M"
            includeMargin
          />
        </div>
        <p className="staff-enroll-meta">
          {minted.token === null
            ? "Reusable link. It doesn't expire."
            : `Expires in ${expiresInMin} min${expiresInMin === 1 ? '' : 's'}`}
        </p>
        <WalletInstallNudge />
        <div className="staff-enroll-actions">
          <button
            type="button"
            className="owner-btn"
            onClick={() => {
              if (navigator.clipboard?.writeText) {
                void navigator.clipboard
                  .writeText(minted.enroll_url)
                  .then(() => toast.success('Link copied'))
                  .catch(() => toast.error('Copy failed'))
              }
            }}
          >
            Copy link
          </button>
          <button
            type="button"
            className="owner-btn owner-btn-primary"
            onClick={reset}
          >
            Done
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="staff-enroll">
      <h1>New customer</h1>
      <p className="stamp-hint">
        Type whatever the customer gives you — anything you skip, they'll
        fill in themselves after scanning the QR.
      </p>
      <form
        className="stamp-form"
        onSubmit={(e) => {
          e.preventDefault()
          if (!mint.isPending) mint.mutate()
        }}
      >
        {multiProgram && (
          <div className="staff-enroll-field">
            <label htmlFor="staff-enroll-program" className="stamp-label">
              Program
            </label>
            <select
              id="staff-enroll-program"
              className="owner-input"
              value={effectiveProgramId ?? ''}
              onChange={(e) => setSelectedProgramId(e.target.value)}
            >
              {activePrograms.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                  {p.pass_type === 'membership' ? ' (membership)' : ''}
                  {p.is_default ? ' (default)' : ''}
                </option>
              ))}
            </select>
          </div>
        )}
        {selectedIsMembership && (
          <p className="stamp-hint" style={{ textAlign: 'left' }}>
            Issuing a membership — collect payment first, then hand off this QR
            for the customer to add their membership pass.
          </p>
        )}
        {needsTier && (
          <div className="staff-enroll-field">
            <label htmlFor="staff-enroll-tier" className="stamp-label">
              Tier <span className="staff-enroll-req"> · required</span>
            </label>
            <select
              id="staff-enroll-tier"
              className="owner-input"
              value={selectedTier}
              onChange={(e) => setSelectedTier(e.target.value)}
            >
              <option value="">Select a tier…</option>
              {tierOptions.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>
        )}
        {enabledKeys.map((key) => (
          <EnrollField
            key={key}
            field={key}
            required={!!onboarding?.[key]?.required}
            value={values[key] ?? ''}
            onChange={(v) => setValues((prev) => ({ ...prev, [key]: v }))}
          />
        ))}
        <button
          type="submit"
          className="owner-btn owner-btn-primary owner-btn-block"
          disabled={mint.isPending || (needsTier && !selectedTier)}
        >
          {mint.isPending ? 'Creating…' : 'Generate QR'}
        </button>
        {needsTier && !selectedTier && (
          <p className="stamp-hint staff-enroll-skip-note">
            Pick the member's tier to generate the QR.
          </p>
        )}
        <p className="stamp-hint staff-enroll-skip-note">
          You can hit Generate without filling anything — the customer can
          do it all on their phone.
        </p>
      </form>

      {/* Available before generating the enrollment QR — if the customer
          has no Google Wallet app, sort that out first. */}
      <WalletInstallNudge />
    </div>
  )
}

function EnrollField({
  field,
  required,
  value,
  onChange,
}: {
  field: OnboardingFieldKey
  required: boolean
  value: string
  onChange: (v: string) => void
}) {
  const label = ONBOARDING_FIELD_LABELS[field]
  const id = `staff-enroll-${field}`
  const placeholder = PLACEHOLDER[field]
  const inputType =
    field === 'email' ? 'email' : field === 'phone' ? 'tel' : 'text'

  // Birthday is MM-DD; render as two small selects so staff can't mistype.
  if (field === 'birthday') {
    return <BirthdayField id={id} value={value} onChange={onChange} note={required ? 'Required for the customer' : 'Optional'} />
  }

  return (
    <div className="staff-enroll-field">
      <label htmlFor={id} className="stamp-label">
        {label}
        {required && <span className="staff-enroll-req"> · required</span>}
      </label>
      <input
        id={id}
        className="owner-input"
        type={inputType}
        autoComplete="off"
        autoCapitalize={field === 'email' ? 'none' : 'words'}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  )
}

const PLACEHOLDER: Record<OnboardingFieldKey, string> = {
  first_name: 'e.g. Maria',
  last_name: 'e.g. Cruz',
  email: 'e.g. maria@example.com',
  phone: 'e.g. 09171234567',
  birthday: '',
}

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
]

function BirthdayField({
  id,
  value,
  onChange,
  note,
}: {
  id: string
  value: string
  onChange: (v: string) => void
  note: string
}) {
  const [m, d] = value.split('-')
  const month = m ?? ''
  const day = d ?? ''

  function update(nextMonth: string, nextDay: string) {
    if (!nextMonth && !nextDay) {
      onChange('')
      return
    }
    if (!nextMonth || !nextDay) {
      // Partial selection — store the partial so the customer can finish
      // it; sanitizer drops malformed values server-side.
      onChange(`${nextMonth}-${nextDay}`)
      return
    }
    onChange(`${nextMonth}-${nextDay}`)
  }

  return (
    <div className="staff-enroll-field">
      <label htmlFor={`${id}-month`} className="stamp-label">
        Birthday <span className="staff-enroll-req"> · {note}</span>
      </label>
      <div className="staff-birthday-row">
        <select
          id={`${id}-month`}
          className="owner-input"
          value={month}
          onChange={(e) => update(e.target.value, day)}
        >
          <option value="">Month</option>
          {MONTHS.map((name, i) => {
            const mm = String(i + 1).padStart(2, '0')
            return (
              <option key={mm} value={mm}>
                {name}
              </option>
            )
          })}
        </select>
        <select
          id={`${id}-day`}
          className="owner-input"
          value={day}
          onChange={(e) => update(month, e.target.value)}
        >
          <option value="">Day</option>
          {Array.from({ length: 31 }, (_, i) => {
            const dd = String(i + 1).padStart(2, '0')
            return (
              <option key={dd} value={dd}>
                {i + 1}
              </option>
            )
          })}
        </select>
      </div>
    </div>
  )
}
